import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getDocuments, runPidQa, getPidTrackers } from "../lib/api";

/*
 ═══════════════════════════════════════════════════════════════
 4-STATE MODEL
   Open    = has failures, needs reviewer attention
   Partial = one audit side signed off, other still open
   Passed  = all checks passed, no action needed
   Closed  = both sides signed off by reviewer(s)
 ═══════════════════════════════════════════════════════════════
*/
function deriveStatus(doc) {
  const rs = doc.reviewerStatus || "";
  if (rs === "CLOSED" || rs === "REVIEWED") return "Closed";
  if (rs === "PARTIALLY_REVIEWED") return "Partial";
  if (rs === "PENDING_PID_QA") return "Pending";
  if (doc.failCount > 0) return "Open";
  return "Passed";
}
const STATUS_STYLE = {
  Open:    { dot:"#ef4444", bg:"#fef2f2", text:"#dc2626", border:"#fecaca" },
  Partial: { dot:"#d97706", bg:"#fffbeb", text:"#b45309", border:"#fde68a" },
  Passed:  { dot:"#22c55e", bg:"#f0fdf4", text:"#16a34a", border:"#bbf7d0" },
  Closed:  { dot:"#6366f1", bg:"#eef2ff", text:"#4f46e5", border:"#c7d2fe" },
  Pending: { dot:"#0891b2", bg:"#ecfeff", text:"#0891b2", border:"#a5f3fc" },
};

/* helper: is this doc a source-typed file (not Combined, not PID_QA)? */
const isSourceFile = d => d.sourceType && d.sourceType !== "Combined" && d.sourceType !== "PID_QA";

/* ── Tiny Components ──────────────────────────────────────────── */

function StatusBadge({ status }) {
  const c = STATUS_STYLE[status] || STATUS_STYLE.Open;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px 3px 8px",
      borderRadius:20, background:c.bg, border:`1px solid ${c.border}`, fontSize:11, fontWeight:600, color:c.text }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:c.dot, flexShrink:0 }}/>{status}
    </span>
  );
}

function ScoreRing({ score, size = 38 }) {
  const s = 3.5, r = (size - s) / 2, circ = 2 * Math.PI * r;
  const off = circ - (score / 100) * circ;
  const col = score >= 70 ? "#16a34a" : score >= 50 ? "#d97706" : score > 0 ? "#dc2626" : "#94a3b8";
  const bg  = score >= 70 ? "#dcfce7" : score >= 50 ? "#fef9c3" : score > 0 ? "#fee2e2" : "#f1f5f9";
  return (
    <div style={{ position:"relative", width:size, height:size, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <svg width={size} height={size} style={{ transform:"rotate(-90deg)", position:"absolute" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={bg} strokeWidth={s}/>
        {score > 0 && <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth={s}
          strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round" style={{ transition:"stroke-dashoffset 0.5s" }}/>}
      </svg>
      <span style={{ fontSize:10, fontWeight:700, color:col, fontFamily:"var(--font-mono)", position:"relative" }}>{score}%</span>
    </div>
  );
}

function AlertIndicator({ status, count }) {
  const has = status === "ALERT" || status === "MANUAL_ALERT";
  if (!has) return <span style={{ color:"var(--text-tertiary)", fontSize:11 }}>—</span>;
  return (
    <span title={`${count} alert${count !== 1 ? "s" : ""}`} style={{
      display:"inline-flex", alignItems:"center", gap:3, padding:"2px 7px", borderRadius:12,
      background:"#fef2f2", border:"1px solid #fecaca", fontSize:10, fontWeight:600, color:"#dc2626", fontFamily:"var(--font-mono)" }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>{count}
    </span>
  );
}

function RulesSummary({ pass, total }) {
  if (!total) return <span style={{ color:"var(--text-tertiary)", fontSize:11 }}>—</span>;
  return (
    <span style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text-secondary)" }}>
      <span style={{ fontWeight:700, color: pass === total ? "#16a34a" : "#dc2626" }}>{pass}</span>
      <span style={{ opacity:0.5 }}>/{total}</span>
    </span>
  );
}

const SOURCE_BADGE = {
  "Usmon Chat Log":         { bg:"#eff6ff", color:"#2563eb", border:"#bfdbfe", short:"CHAT" },
  "Interpretation Report":  { bg:"#f5f3ff", color:"#7c3aed", border:"#ddd6fe", short:"IP" },
  "Technical Report":       { bg:"#ecfeff", color:"#0891b2", border:"#a5f3fc", short:"TECH" },
  "Combined":               { bg:"#f8fafc", color:"#64748b", border:"#e2e8f0", short:"ALL" },
};

function SourceTypeBadge({ sourceType }) {
  if (!sourceType || sourceType === "Combined") return null;
  const s = SOURCE_BADGE[sourceType] || SOURCE_BADGE.Combined;
  return (
    <span style={{ fontSize:9, fontFamily:"var(--font-mono)", fontWeight:600, padding:"1px 5px", borderRadius:3,
      background:s.bg, color:s.color, border:`1px solid ${s.border}`, textTransform:"uppercase", letterSpacing:"0.03em" }}>
      {s.short}
    </span>
  );
}

function Checkbox({ checked, onChange, size = 15, indeterminate }) {
  return (
    <span onClick={e => { e.stopPropagation(); onChange && onChange(!checked); }}
      style={{ width:size, height:size, borderRadius:3, flexShrink:0, cursor:"pointer",
        border:`1.5px solid ${checked || indeterminate ? "#3b82f6" : "var(--border, #cbd5e1)"}`,
        background: checked || indeterminate ? "#3b82f6" : "transparent",
        display:"inline-flex", alignItems:"center", justifyContent:"center", transition:"all 0.1s" }}>
      {checked && <svg width={size-5} height={size-5} viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="3"><path d="M3 8l3.5 3.5L13 5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
      {indeterminate && !checked && <svg width={size-6} height={size-6} viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="3"><line x1="4" y1="8" x2="12" y2="8" strokeLinecap="round"/></svg>}
    </span>
  );
}

/* ── Filter Dropdown ──────────────────────────────────────────── */
function FilterDropdown({ label, selected, options, onChange, width = 150 }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const active = selected.length > 0;
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(""); } };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const list = q ? options.filter(o => o.toLowerCase().includes(q.toLowerCase())) : options;
  const toggle = v => selected.includes(v) ? onChange(selected.filter(x => x !== v)) : onChange([...selected, v]);
  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button onClick={() => setOpen(!open)} style={{
        display:"flex", alignItems:"center", gap:5, padding:"5px 9px", fontSize:11, fontFamily:"var(--font-mono)",
        border:`1px solid ${active ? "#3b82f6" : "var(--border)"}`, borderRadius:6,
        background: active ? "#eff6ff" : "var(--white)", color: active ? "#1d4ed8" : "var(--text-secondary)",
        fontWeight: active ? 600 : 400, cursor:"pointer", outline:"none", minWidth:width, justifyContent:"space-between" }}>
        <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {active ? (selected.length === 1 ? selected[0] : `${selected.length} selected`) : label}
        </span>
        <span style={{ display:"flex", alignItems:"center", gap:3, flexShrink:0 }}>
          {active && <span onClick={e => { e.stopPropagation(); onChange([]); }} style={{ opacity:0.5, display:"flex" }} title="Clear">
            <svg width="9" height="9" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="4" y1="4" x2="12" y2="12" strokeLinecap="round"/><line x1="12" y1="4" x2="4" y2="12" strokeLinecap="round"/></svg>
          </span>}
          <svg width="9" height="9" viewBox="0 0 16 16" fill="none" stroke={active ? "#3b82f6" : "var(--text-tertiary)"} strokeWidth="2"
            style={{ transform: open ? "rotate(180deg)" : "none", transition:"transform 0.15s" }}>
            <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>
      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, width:Math.max(width, 195), maxHeight:250,
          background:"var(--white)", border:"1px solid var(--border)", borderRadius:8,
          boxShadow:"0 8px 24px rgba(0,0,0,0.1)", zIndex:1000, display:"flex", flexDirection:"column" }}>
          {options.length > 5 && <div style={{ padding:"6px 8px", borderBottom:"1px solid var(--border)" }}>
            <input placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} autoFocus
              style={{ width:"100%", boxSizing:"border-box", padding:"5px 8px", fontSize:11, fontFamily:"var(--font-mono)",
                border:"1px solid var(--border)", borderRadius:5, outline:"none", background:"#f9fafb" }}/>
          </div>}
          <div style={{ overflowY:"auto", maxHeight:200 }}>
            {list.length === 0 ? <div style={{ padding:12, fontSize:11, color:"var(--text-tertiary)", textAlign:"center" }}>No matches</div>
            : list.map(opt => {
              const sel = selected.includes(opt);
              return (
                <div key={opt} onClick={() => toggle(opt)} style={{
                  padding:"6px 10px", fontSize:11, fontFamily:"var(--font-mono)", cursor:"pointer",
                  display:"flex", alignItems:"center", gap:7,
                  background: sel ? "#eff6ff" : "transparent", color: sel ? "#1d4ed8" : "var(--text-secondary)",
                  fontWeight: sel ? 500 : 400 }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = "#f9fafb"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = sel ? "#eff6ff" : "transparent"; }}>
                  <Checkbox checked={sel} size={13}/>{opt}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Filter Chip ──────────────────────────────────────────────── */
function Chip({ label, value, onRemove }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"2px 5px 2px 7px", fontSize:10.5,
      fontFamily:"var(--font-mono)", background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe", borderRadius:4 }}>
      <span style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", opacity:0.5 }}>{label}:</span>
      <span style={{ fontWeight:500 }}>{value}</span>
      <span onClick={onRemove} style={{ cursor:"pointer", opacity:0.4, display:"flex", marginLeft:1 }}>
        <svg width="9" height="9" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="4" y1="4" x2="12" y2="12" strokeLinecap="round"/><line x1="12" y1="4" x2="4" y2="12" strokeLinecap="round"/>
        </svg>
      </span>
    </span>
  );
}

/* ── Audit Type Toggle ────────────────────────────────────────── */
const AUDIT_TYPES = [
  { key: "Both",         label: "Both" },
  { key: "Technologist", label: "Tech",             color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
  { key: "IP",           label: "Interp. Physician", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
];

function AuditTypeToggle({ value, onChange }) {
  return (
    <div style={{ display:"inline-flex", borderRadius:7, border:"1px solid var(--border)", overflow:"hidden" }}>
      {AUDIT_TYPES.map((at, i) => {
        const active = value === at.key;
        const accentBg    = active && at.bg    ? at.bg    : active ? "#eff6ff" : "var(--white)";
        const accentColor = active && at.color ? at.color : active ? "#1d4ed8" : "var(--text-secondary)";
        return (
          <button key={at.key} onClick={() => onChange(at.key)} style={{
            padding:"5px 12px", fontSize:11, fontWeight: active ? 600 : 400,
            fontFamily:"var(--font-mono)", cursor:"pointer", border:"none", outline:"none",
            background: accentBg, color: accentColor, position:"relative",
            borderRight: i < AUDIT_TYPES.length - 1 ? "1px solid var(--border)" : "none",
            transition:"all 0.15s",
          }}>
            {at.label}
            {active && <div style={{ position:"absolute", bottom:0, left:"15%", right:"15%", height:2, borderRadius:1, background: accentColor }}/>}
          </button>
        );
      })}
    </div>
  );
}

/* ── Floating Selection Bar (no Close Selected) ───────────────── */
function SelectionBar({ count, onClear, onAudit }) {
  if (!count) return null;
  return (
    <div style={{ position:"fixed", bottom:20, left:"50%", transform:"translateX(-50%)", zIndex:900,
      display:"flex", alignItems:"center", gap:12, padding:"10px 18px",
      background:"var(--white)", border:"1px solid var(--border)", borderRadius:12,
      boxShadow:"0 8px 28px rgba(0,0,0,0.12)", fontFamily:"var(--font-mono)", fontSize:12,
      animation:"floatUp 0.2s ease" }}>
      <span style={{ fontWeight:600, color:"#1d4ed8" }}>{count} selected</span>
      <div style={{ width:1, height:18, background:"var(--border)" }}/>
      <button onClick={onAudit} style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:7,
        background:"#3b82f6", color:"#fff", border:"none", fontSize:11.5, fontWeight:600, fontFamily:"var(--font-mono)",
        cursor:"pointer", boxShadow:"0 2px 6px rgba(59,130,246,0.3)" }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
          <rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 14l2 2 4-4"/>
        </svg>Generate Report
      </button>
      <button onClick={onClear} style={{ padding:"5px 9px", borderRadius:7, background:"transparent",
        color:"var(--text-tertiary)", border:"1px solid var(--border)", fontSize:10.5, fontFamily:"var(--font-mono)", cursor:"pointer" }}>Clear</button>
    </div>
  );
}

/* ── Helper: get score/rules for selected audit type ──────────── */
function getAuditScoreData(doc, auditType) {
  if (auditType === "Technologist") {
    const pass  = doc.techPassCount  || 0;
    const fail  = doc.techFailCount  || 0;
    const total = doc.techTotalRules || 0;
    return { pass, fail, total, score: total > 0 ? Math.round((pass / total) * 100) : 0 };
  }
  if (auditType === "IP") {
    const pass  = doc.ipPassCount  || 0;
    const fail  = doc.ipFailCount  || 0;
    const total = doc.ipTotalRules || 0;
    return { pass, fail, total, score: total > 0 ? Math.round((pass / total) * 100) : 0 };
  }
  // "Both"
  return { pass: doc.passCount || 0, fail: doc.failCount || 0, total: doc.totalRules || 0, score: doc.qaScore || 0 };
}

/* ═══════════════════════════════════════════════════════════════ */
/*  MAIN                                                          */
/* ═══════════════════════════════════════════════════════════════ */

export default function Documents() {
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pidTrackers, setPidTrackers] = useState({});

  // Filters
  const [search, setSearch]       = useState("");
  const [statF, setStatF]         = useState([]);
  const [alertF, setAlertF]       = useState([]);
  const [procF, setProcF]         = useState([]);
  const [facF, setFacF]           = useState([]);
  const [surgF, setSurgF]         = useState([]);
  const [techF, setTechF]         = useState([]);
  const [dateFrom, setDateFrom]   = useState("");
  const [dateTo, setDateTo]       = useState("");
  const [sortBy, setSortBy]       = useState("uploadedAt");
  const [sortDir, setSortDir]     = useState("desc");
  const [sel, setSel]             = useState(new Set());
  const [auditType, setAuditType] = useState("Both");

  useEffect(() => {
    setLoading(true);
    Promise.all([getDocuments({}), getPidTrackers()])
      .then(([docList, trackerList]) => {
        setDocs(docList);
        const tMap = {};
        for (const t of trackerList) tMap[t.id || t.patient_id] = t;
        setPidTrackers(tMap);
      })
      .finally(() => setLoading(false));
  }, []);

  const enriched = useMemo(() => docs.map(d => ({ ...d, _st: deriveStatus(d) })), [docs]);

  /* ── Build PID groups from ALL docs (unfiltered) ──────────── */
  const allPidGroups = useMemo(() => {
    const groups = [];
    const seen = new Map();
    for (const doc of enriched) {
      if (doc.sourceType === "PID_QA") continue;
      const pid = doc.patientId || doc.id;
      if (seen.has(pid)) {
        groups[seen.get(pid)].docs.push(doc);
      } else {
        seen.set(pid, groups.length);
        groups.push({ pid, patientName: doc.patientName || "", docs: [doc] });
      }
    }
    const pidQaDocs = enriched.filter(d => d.sourceType === "PID_QA");
    for (const pqDoc of pidQaDocs) {
      const pid = pqDoc.patientId;
      if (pid && seen.has(pid)) {
        groups[seen.get(pid)].pidQaDoc = pqDoc;
      }
    }
    return groups;
  }, [enriched]);

  /* ── Case-level counts (each PID group = 1 case) ─────────── */
  const caseCounts = useMemo(() => {
    const c = { total: 0, Open: 0, Partial: 0, Passed: 0, Closed: 0, alerts: 0 };
    for (const g of allPidGroups) {
      c.total++;
      const rep = g.pidQaDoc || g.docs[0];
      const st = deriveStatus(rep);
      c[st] = (c[st] || 0) + 1;
      const alertSt = rep.alertStatus || "";
      if (alertSt === "ALERT" || alertSt === "MANUAL_ALERT") c.alerts++;
    }
    return c;
  }, [allPidGroups]);

  /* ── Filter + sort at GROUP level ─────────────────────────── */
  const filteredGroups = useMemo(() => {
    let groups = allPidGroups;

    // Get the "representative" for a group: PID_QA doc if multi-file, else the doc itself
    const rep = g => g.pidQaDoc || g.docs[0];

    if (search) {
      const q = search.toLowerCase();
      groups = groups.filter(g => {
        const r = rep(g);
        return g.docs.some(d => d.filename.toLowerCase().includes(q)) ||
          g.pid.toLowerCase().includes(q) ||
          (r.procedureType || "").toLowerCase().includes(q) ||
          (r.surgeonName || "").toLowerCase().includes(q) ||
          (r.technicianName || "").toLowerCase().includes(q) ||
          (r.facility || "").toLowerCase().includes(q);
      });
    }

    if (statF.length) {
      groups = groups.filter(g => {
        const st = deriveStatus(rep(g));
        return statF.includes(st);
      });
    }

    if (alertF.includes("ALERTS")) {
      groups = groups.filter(g => {
        const r = rep(g);
        return r.alertStatus === "ALERT" || r.alertStatus === "MANUAL_ALERT" ||
          g.docs.some(d => d.alertStatus === "ALERT" || d.alertStatus === "MANUAL_ALERT");
      });
    }

    if (procF.length) groups = groups.filter(g => procF.includes(rep(g).procedureType));
    if (facF.length)  groups = groups.filter(g => facF.includes(rep(g).facility));
    if (surgF.length) groups = groups.filter(g => surgF.includes(rep(g).surgeonName));
    if (techF.length) groups = groups.filter(g => techF.includes(rep(g).technicianName));

    if (dateFrom) {
      const f = new Date(dateFrom);
      groups = groups.filter(g => {
        const d = rep(g).uploadedAt;
        return d && new Date(d) >= f;
      });
    }
    if (dateTo) {
      const t = new Date(dateTo); t.setHours(23,59,59);
      groups = groups.filter(g => {
        const d = rep(g).uploadedAt;
        return d && new Date(d) <= t;
      });
    }

    // Sort
    groups = [...groups].sort((a, b) => {
      const ar = rep(a), br = rep(b);
      let av, bv;
      if (sortBy === "qaScore") {
        av = getAuditScoreData(ar, auditType).score;
        bv = getAuditScoreData(br, auditType).score;
      } else if (sortBy === "uploadedAt") {
        // For groups, use latest upload date among children
        av = a.docs.reduce((lat, d) => (!lat || (d.uploadedAt && d.uploadedAt > lat)) ? d.uploadedAt : lat, "");
        bv = b.docs.reduce((lat, d) => (!lat || (d.uploadedAt && d.uploadedAt > lat)) ? d.uploadedAt : lat, "");
      } else {
        av = ar[sortBy]; bv = br[sortBy];
      }
      if (av == null) return 1; if (bv == null) return -1;
      if (typeof av === "string") { av = av.toLowerCase(); bv = (bv||"").toLowerCase(); }
      return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

    return groups;
  }, [allPidGroups, search, statF, alertF, procF, facF, surgF, techF, dateFrom, dateTo, sortBy, sortDir, auditType]);

  const [expandedPids, setExpandedPids] = useState(new Set());
  const togglePid = pid => setExpandedPids(p => { const s = new Set(p); s.has(pid) ? s.delete(pid) : s.add(pid); return s; });

  // PID QA trigger state
  const [pidQaRunning, setPidQaRunning] = useState(new Set());
  const handleRunPidQa = async (pid, force = false) => {
    setPidQaRunning(p => { const s = new Set(p); s.add(pid); return s; });
    try {
      await runPidQa(pid, "Unknown", force);
      const [fresh, trackerList] = await Promise.all([getDocuments(), getPidTrackers()]);
      setDocs(fresh);
      const tMap = {};
      for (const t of trackerList) tMap[t.id || t.patient_id] = t;
      setPidTrackers(tMap);
    } catch (e) {
      alert("PID QA failed: " + (e?.message || String(e)));
    }
    setPidQaRunning(p => { const s = new Set(p); s.delete(pid); return s; });
  };

  // Unique filter options (from all groups, not filtered)
  const procs = useMemo(() => [...new Set(allPidGroups.map(g => (g.pidQaDoc || g.docs[0]).procedureType).filter(Boolean))].sort(), [allPidGroups]);
  const facs  = useMemo(() => [...new Set(allPidGroups.map(g => (g.pidQaDoc || g.docs[0]).facility).filter(v => v && v !== "Unknown"))].sort(), [allPidGroups]);
  const surgs = useMemo(() => [...new Set(allPidGroups.map(g => (g.pidQaDoc || g.docs[0]).surgeonName).filter(v => v && v !== "Unknown"))].sort(), [allPidGroups]);
  const techs = useMemo(() => [...new Set(allPidGroups.map(g => (g.pidQaDoc || g.docs[0]).technicianName).filter(v => v && v !== "Unknown"))].sort(), [allPidGroups]);

  const toggleSort = c => { if (sortBy === c) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(c); setSortDir("desc"); } };
  const Arr = ({ c: col }) => <span style={{ marginLeft:2, opacity: sortBy === col ? 0.9 : 0.2, fontSize:9 }}>{sortBy === col ? (sortDir === "asc" ? "▲" : "▼") : "▲"}</span>;

  // Selection — for PID groups, select the PID_QA virtual doc ID (for accurate audit reports)
  const togSel = id => setSel(p => { const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const getGroupSelId = g => g.pidQaDoc ? g.pidQaDoc.id : g.docs[0]?.id;

  const allSel = filteredGroups.length > 0 && filteredGroups.every(g => {
    if (g.docs.length === 1) return sel.has(g.docs[0].id);
    return sel.has(getGroupSelId(g));
  });
  const someSel = filteredGroups.some(g => {
    if (g.docs.length === 1) return sel.has(g.docs[0].id);
    return sel.has(getGroupSelId(g));
  });
  const togAll = () => {
    setSel(p => {
      const s = new Set(p);
      if (allSel) {
        filteredGroups.forEach(g => {
          if (g.docs.length === 1) s.delete(g.docs[0].id);
          else s.delete(getGroupSelId(g));
        });
      } else {
        filteredGroups.forEach(g => {
          if (g.docs.length === 1) s.add(g.docs[0].id);
          else s.add(getGroupSelId(g));
        });
      }
      return s;
    });
  };

  const hasF = statF.length + alertF.length + procF.length + facF.length + surgF.length + techF.length + (dateFrom?1:0) + (dateTo?1:0) > 0;
  const clearAll = () => { setStatF([]); setAlertF([]); setProcF([]); setFacF([]); setSurgF([]); setTechF([]); setDateFrom(""); setDateTo(""); setSearch(""); };

  const auditLabel = auditType === "Technologist" ? "Tech" : auditType === "IP" ? "IP" : "";

  const fmtDate = d => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

  return (
    <>
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">Documents</div>
          <div className="page-subtitle" style={{ fontSize:12, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)" }}>
            {caseCounts.total} total · {caseCounts.Open > 0 ? `${caseCounts.Open} open` : "none open"}{caseCounts.alerts > 0 ? ` · ${caseCounts.alerts} with alerts` : ""}
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary btn-sm" onClick={() => nav("/upload")}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" style={{width:14,height:14}}><line x1="8" y1="3" x2="8" y2="13"/><line x1="3" y1="8" x2="13" y2="8"/></svg>
            Upload
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* Row 1: Search + status pills + alert toggle */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8, flexWrap:"wrap" }}>
          <div style={{ position:"relative", flex:1, maxWidth:300 }}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5" style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)" }}>
              <circle cx="7" cy="7" r="5"/><path d="M12 12l3 3" strokeLinecap="round"/>
            </svg>
            <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ width:"100%", boxSizing:"border-box", padding:"7px 10px 7px 30px", fontSize:12, fontFamily:"var(--font-mono)",
                border:"1px solid var(--border)", borderRadius:7, outline:"none", background:"var(--white)", color:"var(--text-primary)" }}/>
          </div>
          <div style={{ display:"flex", gap:3 }}>
            {[{k:null,l:"All",n:caseCounts.total},{k:"Open",l:"Open",n:caseCounts.Open},{k:"Partial",l:"Partial",n:caseCounts.Partial},{k:"Passed",l:"Passed",n:caseCounts.Passed},{k:"Closed",l:"Closed",n:caseCounts.Closed}].map(p => {
              const a = p.k === null ? statF.length === 0 : statF.includes(p.k);
              return (<button key={p.l} onClick={() => p.k === null ? setStatF([]) : setStatF(pr => pr.includes(p.k) ? pr.filter(x => x !== p.k) : [...pr, p.k])}
                style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 10px", borderRadius:6, cursor:"pointer",
                  border:`1px solid ${a ? "#3b82f6" : "var(--border)"}`, background: a ? "#eff6ff" : "var(--white)",
                  color: a ? "#1d4ed8" : "var(--text-secondary)", fontSize:11, fontWeight: a ? 600 : 400, fontFamily:"var(--font-mono)" }}>
                {p.k && <span style={{ width:6, height:6, borderRadius:"50%", background:STATUS_STYLE[p.k]?.dot }}/>}
                {p.l} <span style={{ fontSize:9.5, opacity:0.6, fontWeight:700 }}>{p.n}</span>
              </button>);
            })}
            <div style={{ width:1, height:22, background:"var(--border)", margin:"0 2px", alignSelf:"center" }}/>
            <button onClick={() => setAlertF(p => p.includes("ALERTS") ? [] : ["ALERTS"])} style={{
              display:"flex", alignItems:"center", gap:4, padding:"5px 10px", borderRadius:6, cursor:"pointer",
              border:`1px solid ${alertF.includes("ALERTS") ? "#fecaca" : "var(--border)"}`,
              background: alertF.includes("ALERTS") ? "#fef2f2" : "var(--white)",
              color: alertF.includes("ALERTS") ? "#dc2626" : "var(--text-secondary)",
              fontSize:11, fontWeight: alertF.includes("ALERTS") ? 600 : 400, fontFamily:"var(--font-mono)" }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              Alerts {caseCounts.alerts > 0 && <span style={{ fontSize:9.5, fontWeight:700, opacity:0.7 }}>{caseCounts.alerts}</span>}
            </button>
          </div>
        </div>

        {/* Row 2: Audit Type toggle + dropdown filters */}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom: hasF ? 6 : 12, flexWrap:"wrap" }}>
          <AuditTypeToggle value={auditType} onChange={setAuditType}/>
          <div style={{ width:1, height:20, background:"var(--border)", margin:"0 2px" }}/>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5"><path d="M2 3h12M4 8h8M6 13h4" strokeLinecap="round"/></svg>
          <FilterDropdown label="Procedure" selected={procF} options={procs} onChange={setProcF}/>
          {facs.length > 0 && <FilterDropdown label="Facility" selected={facF} options={facs} onChange={setFacF}/>}
          {surgs.length > 0 && <FilterDropdown label="Surgeon" selected={surgF} options={surgs} onChange={setSurgF}/>}
          {techs.length > 0 && <FilterDropdown label="Technologist" selected={techF} options={techs} onChange={setTechF}/>}
          <div style={{ width:1, height:20, background:"var(--border)", margin:"0 1px" }}/>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>FROM</span>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ width:120, fontSize:10.5, fontFamily:"var(--font-mono)", padding:"4px 6px", border:"1px solid var(--border)", borderRadius:5, color:"var(--text-secondary)", background:"var(--white)", outline:"none" }}/>
            <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>TO</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{ width:120, fontSize:10.5, fontFamily:"var(--font-mono)", padding:"4px 6px", border:"1px solid var(--border)", borderRadius:5, color:"var(--text-secondary)", background:"var(--white)", outline:"none" }}/>
          </div>
          {hasF && <button onClick={clearAll} style={{ fontSize:10.5, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", background:"none", border:"none", cursor:"pointer", textDecoration:"underline" }}>Clear all</button>}
        </div>

        {/* Active chips */}
        {hasF && (
          <div style={{ display:"flex", gap:4, marginBottom:10, flexWrap:"wrap", alignItems:"center" }}>
            {statF.map(s => <Chip key={s} label="Status" value={s} onRemove={() => setStatF(statF.filter(x => x !== s))}/>)}
            {alertF.map(a => <Chip key={a} label="Alert" value="Has Alerts" onRemove={() => setAlertF([])}/>)}
            {procF.map(v => <Chip key={v} label="Procedure" value={v} onRemove={() => setProcF(procF.filter(x => x !== v))}/>)}
            {facF.map(v => <Chip key={v} label="Facility" value={v} onRemove={() => setFacF(facF.filter(x => x !== v))}/>)}
            {surgF.map(v => <Chip key={v} label="Surgeon" value={v} onRemove={() => setSurgF(surgF.filter(x => x !== v))}/>)}
            {techF.map(v => <Chip key={v} label="Tech" value={v} onRemove={() => setTechF(techF.filter(x => x !== v))}/>)}
            {(dateFrom||dateTo) && <Chip label="Date" value={`${dateFrom||"..."} → ${dateTo||"..."}`} onRemove={() => { setDateFrom(""); setDateTo(""); }}/>}
            <span style={{ fontSize:10.5, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)", marginLeft:3 }}>{filteredGroups.length} of {caseCounts.total}</span>
          </div>
        )}

        {/* Table */}
        <div className="card">
          <div className="table-wrap">
            {loading ? (
              <div style={{ padding:32 }}>{[0,1,2,3,4].map(i => <div key={i} style={{ display:"flex", gap:12, marginBottom:14, alignItems:"center" }}><div className="skeleton" style={{ height:12, width:16 }}/><div className="skeleton" style={{ height:12, flex:2 }}/><div className="skeleton" style={{ height:12, width:80 }}/><div className="skeleton" style={{ height:12, width:40 }}/><div className="skeleton" style={{ height:12, width:70 }}/></div>)}</div>
            ) : filteredGroups.length === 0 ? (
              <div style={{ padding:48, textAlign:"center", color:"var(--text-tertiary)" }}>
                <div style={{ fontSize:14, marginBottom:4 }}>No documents found</div>
                <div style={{ fontSize:12 }}>{hasF ? "Try adjusting your filters" : "Upload a document to get started"}</div>
              </div>
            ) : (
              <table>
                <thead><tr>
                  <th style={{ width:32, textAlign:"center", padding:"8px 4px" }}><Checkbox checked={allSel} indeterminate={someSel && !allSel} onChange={togAll}/></th>
                  <th onClick={() => toggleSort("filename")} style={{ cursor:"pointer" }}>Document <Arr c="filename"/></th>
                  <th onClick={() => toggleSort("procedureType")} style={{ cursor:"pointer", width:125 }}>Procedure <Arr c="procedureType"/></th>
                  <th onClick={() => toggleSort("qaScore")} style={{ cursor:"pointer", width:68, textAlign:"center" }}>
                    {auditLabel && <span style={{ fontSize:9, fontWeight:700, opacity:0.5 }}>{auditLabel} </span>}Score <Arr c="qaScore"/>
                  </th>
                  <th style={{ width:85, textAlign:"center" }}>Status</th>
                  <th style={{ width:60, textAlign:"center" }}>Alerts</th>
                  <th style={{ width:68, textAlign:"center" }}>
                    {auditLabel && <span style={{ fontSize:9, fontWeight:700, opacity:0.5 }}>{auditLabel} </span>}Rules
                  </th>
                  <th onClick={() => toggleSort("uploadedAt")} style={{ cursor:"pointer", width:112 }}>Uploaded <Arr c="uploadedAt"/></th>
                  <th style={{ width:105 }}>Uploaded By</th>
                  <th style={{ width:50 }}></th>
                </tr></thead>
                <tbody>{filteredGroups.map(group => {
                  // Single-file group → flat row
                  if (group.docs.length === 1 && !isSourceFile(group.docs[0])) {
                    const doc = group.docs[0];
                    const isSel = sel.has(doc.id);
                    const ad = getAuditScoreData(doc, auditType);
                    return (
                      <tr key={doc.id} onClick={() => nav(`/documents/${encodeURIComponent(doc.id)}${auditType !== "Both" ? `?audit=${auditType}` : ""}`)}
                        style={{ cursor:"pointer", background: isSel ? "#eff6ff" : undefined, transition:"background 0.1s" }}>
                        <td style={{ textAlign:"center", padding:"8px 4px" }}><Checkbox checked={isSel} onChange={() => togSel(doc.id)}/></td>
                        <td style={{ maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontWeight:500, fontSize:12.5 }}>
                          {doc.filename}
                          {isSourceFile(doc) && <> <SourceTypeBadge sourceType={doc.sourceType}/></>}
                        </td>
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{isSrcTyped ? "—" : (doc.procedureType || "—")}</td>
                        <td style={{ textAlign:"center" }}><div style={{ display:"flex", justifyContent:"center" }}><ScoreRing score={ad.score}/></div></td>
                        <td style={{ textAlign:"center" }}><StatusBadge status={doc._st}/></td>
                        <td style={{ textAlign:"center" }}><AlertIndicator status={doc.alertStatus} count={doc.alertCount || 0}/></td>
                        <td style={{ textAlign:"center" }}><RulesSummary pass={ad.pass} total={ad.total}/></td>
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{fmtDate(doc.uploadedAt)}</td>
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{doc.uploadedBy || "—"}</td>
                        <td><button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); nav(`/documents/${encodeURIComponent(doc.id)}${auditType !== "Both" ? `?audit=${auditType}` : ""}`); }} style={{ fontSize:10.5 }}>{doc._st === "Open" ? "Review" : "View"}</button></td>
                      </tr>
                    );
                  }

                  // Multi-file group → expandable
                  const isExpanded = expandedPids.has(group.pid);
                  const groupDocs = group.docs;
                  const pqDoc = group.pidQaDoc;
                  const hasPidQa = !!pqDoc;
                  const isRunningQa = pidQaRunning.has(group.pid);

                  const groupSelId = getGroupSelId(group);
                  const isGroupSel = sel.has(groupSelId);

                  const gAd = hasPidQa ? getAuditScoreData(pqDoc, auditType) : { score: null, pass: 0, fail: 0, total: 0 };
                  const gScore = gAd.score;
                  const gAlerts = hasPidQa ? (pqDoc.alertCount || 0) : groupDocs.reduce((n, d) => n + (d.alertCount || 0), 0);
                  const gAlertStatus = hasPidQa
                    ? (pqDoc.alertStatus || "NO_ALERT")
                    : groupDocs.some(d => d.alertStatus === "ALERT" || d.alertStatus === "MANUAL_ALERT") ? "ALERT" : "NO_ALERT";
                  const gStatus = hasPidQa ? (pqDoc._st || (pqDoc.failCount > 0 ? "Open" : "Passed")) : "Open";
                  const gProc = (hasPidQa ? pqDoc.procedureType : groupDocs[0]?.procedureType) || "—";
                  const gDate = groupDocs.reduce((latest, d) => (!latest || (d.uploadedAt && d.uploadedAt > latest)) ? d.uploadedAt : latest, "");
                  const gUploader = groupDocs[0]?.uploadedBy || "—";
                  const displayName = group.patientName || group.pid;
                  const pidDocId = hasPidQa ? pqDoc.id : null;

                  return (
                    <React.Fragment key={`pid-${group.pid}`}>
                      <tr onClick={() => hasPidQa ? nav(`/documents/${encodeURIComponent(pidDocId)}${auditType !== "Both" ? `?audit=${auditType}` : ""}`) : togglePid(group.pid)}
                        style={{ cursor:"pointer", background:"var(--gray-50, #f9fafb)", borderBottom:"1px solid var(--border)" }}>
                        <td style={{ textAlign:"center", padding:"8px 4px" }} onClick={e => e.stopPropagation()}>
                          <Checkbox checked={isGroupSel} onChange={() => togSel(groupSelId)}/>
                        </td>
                        <td style={{ fontWeight:600, fontSize:12.5 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round"
                              style={{ transition:"transform 0.15s", transform: isExpanded ? "rotate(90deg)" : "none", flexShrink:0 }}
                              onClick={e => { e.stopPropagation(); togglePid(group.pid); }}>
                              <path d="M6 4l4 4-4 4"/>
                            </svg>
                            <span>{displayName}</span>
                            <span style={{ fontSize:9.5, fontFamily:"var(--font-mono)", fontWeight:600, color:"#3b82f6", background:"#eff6ff",
                              padding:"1px 6px", borderRadius:10, border:"1px solid #bfdbfe" }}>
                              {groupDocs.length} files
                            </span>
                          </div>
                        </td>
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{gProc}</td>
                        <td style={{ textAlign:"center" }}>
                          {gScore != null
                            ? <div style={{ display:"flex", justifyContent:"center" }}><ScoreRing score={gScore}/></div>
                            : <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>—</span>}
                        </td>
                        <td style={{ textAlign:"center" }}>
                          {hasPidQa
                            ? <StatusBadge status={gStatus}/>
                            : (() => {
                                const tracker = pidTrackers[group.pid];
                                const ts = tracker?.status;
                                const rcv = Object.values(tracker?.received_files || {}).filter(Boolean).length;
                                const exp = tracker?.expected_sources?.length;
                                if (ts === "QA_RUNNING" || isRunningQa)
                                  return <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"#3b82f6", background:"#eff6ff", padding:"2px 8px", borderRadius:10, border:"1px solid #bfdbfe", whiteSpace:"nowrap" }}>QA Running</span>;
                                if (ts === "WAITING" && exp && rcv < exp)
                                  return <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"#0891b2", background:"#ecfeff", padding:"2px 8px", borderRadius:10, border:"1px solid #a5f3fc", whiteSpace:"nowrap" }}>
                                    Extracting {rcv}/{exp}
                                  </span>;
                                return <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"#d97706", background:"#fffbeb", padding:"2px 8px", borderRadius:10, border:"1px solid #fde68a", whiteSpace:"nowrap" }}>Pending QA</span>;
                              })()
                          }
                        </td>
                        <td style={{ textAlign:"center" }}><AlertIndicator status={gAlertStatus} count={gAlerts}/></td>
                        <td style={{ textAlign:"center" }}>
                          {hasPidQa ? <RulesSummary pass={gAd.pass} total={gAd.total}/> : <span style={{ color:"var(--text-tertiary)", fontSize:11 }}>—</span>}
                        </td>
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{fmtDate(gDate)}</td>
                        <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{gUploader}</td>
                        <td onClick={e => e.stopPropagation()}>
                          {hasPidQa
                            ? <button className="btn btn-ghost btn-sm" onClick={() => nav(`/documents/${encodeURIComponent(pidDocId)}${auditType !== "Both" ? `?audit=${auditType}` : ""}`)} style={{ fontSize:10.5 }}>{gStatus === "Open" ? "Review" : "View"}</button>
                            : (() => {
                                const tracker = pidTrackers[group.pid];
                                const ts = tracker?.status;
                                if (ts === "QA_RUNNING" || isRunningQa)
                                  return <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--blue-500)", fontWeight:600 }}>Running…</span>;
                                if (ts === "WAITING" && tracker?.expected_sources) {
                                  const rcv = Object.values(tracker?.received_files || {}).filter(Boolean).length;
                                  const exp = tracker.expected_sources.length;
                                  if (rcv < exp)
                                    return <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>
                                      {rcv}/{exp} files
                                    </span>;
                                  return <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>QA queued</span>;
                                }
                                if (ts === "READY")
                                  return <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>QA queued</span>;
                                // All extracted or no tracker (single uploads) — show Run QA
                                const allExtracted = groupDocs.every(d => d.alertStatus === "PENDING_PID_QA");
                                if (allExtracted)
                                  return <button className="btn btn-primary btn-sm" onClick={() => handleRunPidQa(group.pid, true)}
                                    disabled={isRunningQa} style={{ fontSize:10, fontFamily:"var(--font-mono)", padding:"3px 10px" }}>
                                    Run QA
                                  </button>;
                                return <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>Extracting…</span>;
                              })()
                          }
                        </td>
                      </tr>
                      {isExpanded && groupDocs.map(doc => {
                        const isSel = sel.has(doc.id);
                        const isSrcTyped = isSourceFile(doc);
                        return (
                          <tr key={doc.id} onClick={() => nav(`/documents/${encodeURIComponent(doc.id)}${auditType !== "Both" ? `?audit=${auditType}` : ""}`)}
                            style={{ cursor:"pointer", background: isSel ? "#eff6ff" : "var(--white)", transition:"background 0.1s" }}>
                            <td style={{ textAlign:"center", padding:"8px 4px" }}><Checkbox checked={isSel} onChange={() => togSel(doc.id)}/></td>
                            <td style={{ maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontSize:12, paddingLeft:28 }}>
                              <span style={{ color:"var(--text-tertiary)", marginRight:4 }}>└</span>
                              <span style={{ fontWeight:400 }}>{doc.filename}</span>
                              {doc.sourceType && <> <SourceTypeBadge sourceType={doc.sourceType}/></>}
                            </td>
                            <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{isSrcTyped ? "—" : (doc.procedureType || "—")}</td>
                            <td style={{ textAlign:"center" }}>
                              {isSrcTyped
                                ? <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>—</span>
                                : <div style={{ display:"flex", justifyContent:"center" }}><ScoreRing score={getAuditScoreData(doc, auditType).score}/></div>}
                            </td>
                            <td style={{ textAlign:"center" }}>
                              {isSrcTyped
                                ? <span style={{ fontSize:9.5, fontFamily:"var(--font-mono)", color:"#0891b2", background:"#ecfeff", padding:"2px 6px", borderRadius:10, border:"1px solid #a5f3fc" }}>Extracted</span>
                                : <StatusBadge status={doc._st}/>}
                            </td>
                            <td style={{ textAlign:"center" }}><AlertIndicator status={doc.alertStatus} count={doc.alertCount || 0}/></td>
                            <td style={{ textAlign:"center" }}>
                              {isSrcTyped
                                ? <span style={{ color:"var(--text-tertiary)", fontSize:11 }}>—</span>
                                : <RulesSummary pass={getAuditScoreData(doc, auditType).pass} total={getAuditScoreData(doc, auditType).total}/>}
                            </td>
                            <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{fmtDate(doc.uploadedAt)}</td>
                            <td style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>{doc.uploadedBy || "—"}</td>
                            <td><button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); nav(`/documents/${encodeURIComponent(doc.id)}${auditType !== "Both" ? `?audit=${auditType}` : ""}`); }} style={{ fontSize:10.5 }}>View</button></td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}</tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <SelectionBar count={sel.size} onClear={() => setSel(new Set())} onAudit={() => nav(`/audit-files?ids=${[...sel].join(",")}`)}/>
      <style>{`@keyframes floatUp { from { opacity:0; transform:translateX(-50%) translateY(16px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>
    </>
  );
}